import { useEffect, useMemo, useState, type ReactNode } from "react";
import { skipToken, useQuery } from "@tanstack/react-query";
import { Download, FolderSearch, Loader2, X } from "lucide-react";
import { getFolders } from "@/api/stock";
import {
  getFolderCounts,
  kombiPreview,
  setPreview,
  type CarrierTier,
  type KundenversandOption,
  type SetPreviewRequest,
  type SetPreviewResult,
  type KombiPreviewRequest,
  type KombiPreviewResult,
  type KombiPreviewRow,
} from "@/api/pricing";
import { ApiError } from "@/api/client";
import { FolderTree } from "@/components/FolderTree";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  buildFolderPathMap,
  buildTreeFromFolders,
  countsBySubtreeFromCounts,
  findFolderNode,
  prettyFolderLabel,
  subtreeFolderIds,
} from "@/lib/folderTree";
import { downloadCsvDe } from "@/lib/csv";
import { cn } from "@/lib/utils";
import { SetPreviewTable } from "./SetPreviewTable";
import { buildVerkaufspreisCsv, verkaufspreisFilename } from "./csv";
import { KombiBuilder, kombiLabel, type SelectedKombi } from "./KombiBuilder";

/** Start: nur 1 Stück. Weitere Mengen fügt der Nutzer frei hinzu. */
const START_QUANTITIES = [1];
/** Gleiche Grenze wie `setPreviewSchema.quantities.max` (Backend). */
const MAX_QUANTITIES = 10;
/** Schnellwahl: fügt nur hinzu, entfernt nie. */
const SUGGESTIONS = [5, 10, 50, 100] as const;
/** Gleiche Grenze wie `setPreviewSchema.folder_ids.max` — darüber fragt die Seite nicht ab (P7). */
const MAX_FOLDERS = 2000;
const CUSTOM_KEY = "custom";

/** Ganze Zahl 1–1000, sonst null. */
function parseQuantity(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const n = Number(text.trim());
  return n >= 1 && n <= 1000 ? n : null;
}

/** Betrag 0–1000 mit höchstens 2 Nachkommastellen (Komma oder Punkt), sonst null. */
function parseBetrag(text: string): number | null {
  const t = text.trim();
  if (!/^\d+([.,]\d{1,2})?$/.test(t)) return null;
  const n = Number(t.replace(",", "."));
  return n <= 1000 ? n : null;
}

export default function VerkaufspreisPage() {
  // Gewählter Ordner per id — gleichnamige Ordner bleiben unterscheidbar (Geldpfad: folder_ids).
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Gewählte Stückzahlen, immer aufsteigend und ohne Duplikate — so gehen sie auch ans Backend.
  const [quantities, setQuantities] = useState<number[]>(START_QUANTITIES);
  const [addText, setAddText] = useState("");
  const [addNotice, setAddNotice] = useState<{ text: string; bad: boolean } | null>(null);
  const [kvKey, setKvKey] = useState<string | null>(null);
  const [customText, setCustomText] = useState("");
  const [custom, setCustom] = useState<number | null>(null);
  const [tiers, setTiers] = useState<Record<string, CarrierTier>>({});
  const [kombis, setKombis] = useState<SelectedKombi[]>([]);
  const [kombiTiers, setKombiTiers] = useState<Record<string, CarrierTier>>({});

  const foldersQuery = useQuery({ queryKey: ["stock-folders"], queryFn: getFolders, staleTime: 5 * 60_000 });
  const countsQuery = useQuery({ queryKey: ["pricing-folder-counts"], queryFn: getFolderCounts, staleTime: 60_000 });

  const tree = useMemo(() => buildTreeFromFolders(foldersQuery.data ?? []), [foldersQuery.data]);
  const pathMap = useMemo(() => buildFolderPathMap(foldersQuery.data ?? []), [foldersQuery.data]);
  const counts = countsQuery.data;
  const countsById = useMemo(() => countsBySubtreeFromCounts(tree, counts ?? {}), [tree, counts]);
  const totalCount = useMemo(() => Object.values(counts ?? {}).reduce((a, b) => a + b, 0), [counts]);

  const node = useMemo(() => findFolderNode(tree, selectedId), [tree, selectedId]);
  const folderIds = useMemo(() => (node ? subtreeFolderIds(node) : []), [node]);
  const tooBig = folderIds.length > MAX_FOLDERS;

  const kundenversand = kvKey === CUSTOM_KEY ? custom : null;

  const request = useMemo<SetPreviewRequest | null>(() => {
    if (!node || tooBig || quantities.length === 0) return null;
    return { folder_ids: folderIds, quantities, kundenversand, tiers };
  }, [node, tooBig, folderIds, quantities, kundenversand, tiers]);

  const folderKey = folderIds.join(",");
  const preview = useQuery({
    queryKey: ["pricing-set-preview", folderKey, request],
    queryFn: request ? () => setPreview(request) : skipToken,
    // Innerhalb desselben Ordners bleibt die Tabelle beim Neurechnen stehen (Stufe, Stückzahl);
    // ein Ordnerwechsel zeigt dagegen nie die Zeilen des alten Ordners.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === folderKey ? prev : undefined),
  });
  // Letzte erfolgreiche Antwort je Ordner: scheitert ein Neuabruf (Stufe, Stückzahl), bleibt die
  // Tabelle sichtbar, wird aber als veraltet markiert und ist nicht exportierbar.
  const [lastGood, setLastGood] = useState<{ folderKey: string; data: SetPreviewResult } | null>(null);
  useEffect(() => {
    if (preview.isSuccess && !preview.isPlaceholderData) setLastGood({ folderKey, data: preview.data });
  }, [preview.isSuccess, preview.isPlaceholderData, preview.data, folderKey]);
  const fallback = preview.error && lastGood?.folderKey === folderKey ? lastGood.data : undefined;
  const data = request !== null ? (preview.data ?? fallback) : undefined;
  const stale = request !== null && preview.error !== null && data !== undefined;

  const kombiRequest = useMemo<KombiPreviewRequest | null>(() => kombis.length ? {
    kombis: kombis.map(k => ({ key: k.key, components: k.components.map(c => ({ sku: c.sku, qty: c.qty })) })),
    quantities, kundenversand, tiers: kombiTiers,
  } : null, [kombis, quantities, kundenversand, kombiTiers]);
  const kombiSelection = JSON.stringify(kombiRequest?.kombis ?? []);
  const kombiQuery = useQuery({
    queryKey: ["pricing-kombi-preview", kombiRequest],
    queryFn: kombiRequest ? () => kombiPreview(kombiRequest) : skipToken,
    placeholderData: (prev, prevQuery) => JSON.stringify((prevQuery?.queryKey[1] as KombiPreviewRequest | null)?.kombis ?? []) === kombiSelection ? prev : undefined,
  });
  const [lastGoodKombi, setLastGoodKombi] = useState<{ selection: string; data: KombiPreviewResult } | null>(null);
  useEffect(() => {
    if (kombiQuery.isSuccess && !kombiQuery.isPlaceholderData) setLastGoodKombi({ selection: kombiSelection, data: kombiQuery.data });
  }, [kombiQuery.isSuccess, kombiQuery.isPlaceholderData, kombiQuery.data, kombiSelection]);
  const kombiFallback = kombiQuery.error && lastGoodKombi?.selection === kombiSelection ? lastGoodKombi.data : undefined;
  const kombiData = kombiRequest ? kombiQuery.data ?? kombiFallback : undefined;
  const canExport = ((data?.rows.length ?? 0) + (kombiData?.rows.length ?? 0)) > 0
    && (kombiData?.missing.length ?? 0) === 0
    && (!request || (!preview.error && !preview.isPlaceholderData && !preview.isLoading))
    && (!kombiRequest || (!kombiQuery.error && !kombiQuery.isPlaceholderData && !kombiQuery.isLoading));

  const tableOptionen = data?.kundenversand_optionen ?? kombiData?.kundenversand_optionen ?? [];
  const optionen = tableOptionen.filter((o) => o.key !== CUSTOM_KEY);
  const effectiveKey = kvKey ?? optionen[0]?.key ?? "";

  function selectFolder(id: number | null) {
    // Stufen nur bei echtem Ordnerwechsel verwerfen; erneuter Klick auf denselben Ordner behält sie.
    if (id === selectedId) return;
    setSelectedId(id);
    setTiers({});
  }

  const full = quantities.length >= MAX_QUANTITIES;

  function addQuantity(q: number) {
    if (quantities.includes(q)) {
      setAddNotice({ text: `${q} ist schon gewählt`, bad: false });
      setAddText("");
      return;
    }
    if (full) return;
    setQuantities([...quantities, q].sort((a, b) => a - b));
    setAddNotice(null);
    setAddText("");
  }

  function addFromInput() {
    if (full) return;
    if (addText.trim() === "") {
      setAddNotice({ text: "Zahl 1–1000 eingeben", bad: true });
      return;
    }
    const q = parseQuantity(addText);
    if (q === null) {
      setAddNotice({ text: "Ganze Zahl 1–1000", bad: true });
      return;
    }
    addQuantity(q);
  }

  function removeQuantity(q: number) {
    // Das Backend braucht mindestens eine Menge — die letzte bleibt stehen.
    if (quantities.length <= 1) return;
    setQuantities(quantities.filter((x) => x !== q));
    setAddNotice(null);
  }

  function onCustomChange(text: string) {
    setCustomText(text);
    if (text.trim() === "") {
      setCustom(null);
      return;
    }
    const n = parseBetrag(text);
    if (n !== null) setCustom(n);
  }

  const addInvalid = (addNotice?.bad ?? false) || (addText.trim() !== "" && parseQuantity(addText) === null);
  const suggestions = full ? [] : SUGGESTIONS.filter((q) => !quantities.includes(q));
  const customInvalid = customText.trim() !== "" && parseBetrag(customText) === null;

  function exportCsv() {
    if (!canExport) return;
    downloadCsvDe(verkaufspreisFilename(node && (data?.rows.length ?? 0) > 0 ? node.name : "kombi", new Date()), buildVerkaufspreisCsv(data, kombiData));
  }

  const folderTitle = node ? (pathMap.get(node.id) ?? [node.name]).map(prettyFolderLabel).join(" / ") : null;

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-ink">Verkaufspreis</h1>
          <p className="text-sm text-ink-2 mt-1">
            Set-Preise je Ordner: Break-Even, VK mit Gratisversand und Plus-Versand-Preis. Nur Ansicht, nichts wird gespeichert.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 shrink-0"
          disabled={!canExport}
          onClick={exportCsv}
        >
          <Download className="size-3.5" />
          CSV exportieren
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-start">
        <FolderTree
          tree={tree}
          selectedId={node ? node.id : null}
          onSelect={selectFolder}
          countsById={countsById}
          totalCount={totalCount}
          readOnly
        />

        <div className="min-w-0 space-y-3">
          {/* Steuerleiste */}
          <div className="rounded-md border border-line bg-panel px-4 py-3 flex flex-wrap items-end gap-x-6 gap-y-3">
            <fieldset className="space-y-1">
              <legend className="text-[11px] uppercase tracking-wide text-ink-2">Stückzahl</legend>
              <div className="flex flex-wrap items-center gap-1.5">
                <ul aria-label="Gewählte Stückzahlen" className="flex flex-wrap items-center gap-1.5">
                  {quantities.map((q) => {
                    const removable = quantities.length > 1;
                    return (
                      <li
                        key={q}
                        className="h-8 min-w-9 inline-flex items-center rounded-md border border-primary bg-primary-soft font-mono text-sm font-semibold text-primary"
                      >
                        <span className={cn("pl-2.5", removable ? "pr-0.5" : "pr-2.5")}>{q}</span>
                        {removable && (
                          <button
                            type="button"
                            aria-label={`Stückzahl ${q} entfernen`}
                            onClick={() => removeQuantity(q)}
                            className="h-full px-1.5 rounded-r-md text-primary/60 transition-colors hover:text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          >
                            <X className="size-3" aria-hidden="true" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <Input
                  aria-label="Stückzahl hinzufügen"
                  aria-invalid={addInvalid}
                  inputMode="numeric"
                  placeholder="z. B. 12"
                  value={addText}
                  onChange={(e) => {
                    setAddText(e.target.value);
                    setAddNotice(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    addFromInput();
                  }}
                  className="h-8 w-20 font-mono"
                />
                <Button size="sm" variant="outline" className="h-8" disabled={full} onClick={addFromInput}>
                  Hinzufügen
                </Button>
                {suggestions.map((q) => (
                  <button
                    key={q}
                    type="button"
                    aria-label={`${q} Stück hinzufügen`}
                    onClick={() => addQuantity(q)}
                    className="h-8 px-1 rounded font-mono text-xs text-ink-2 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    +{q}
                  </button>
                ))}
              </div>
              <p className={cn("text-[11px] h-4", addNotice?.bad ? "text-coral" : full ? "text-warn" : "text-ink-2")}>
                {addNotice?.text ?? (full ? "max. 10 Stückzahlen" : "1–1000, bis zu 10 Stückzahlen")}
              </p>
            </fieldset>

            <fieldset className="space-y-1">
              <legend className="text-[11px] uppercase tracking-wide text-ink-2">Kundenversand (Plus-Versand)</legend>
              <div className="flex items-center gap-1.5">
                <select
                  aria-label="Kundenversand"
                  value={effectiveKey}
                  onChange={(e) => setKvKey(e.target.value)}
                  className="h-8 rounded-md border border-line bg-panel px-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {effectiveKey === "" && <option value="" disabled>—</option>}
                  {optionen.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label} · {o.betrag.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </option>
                  ))}
                  <option value={CUSTOM_KEY}>eigener Betrag</option>
                </select>
                {kvKey === CUSTOM_KEY && (
                  <Input
                    aria-label="Kundenversand eigener Betrag"
                    aria-invalid={customInvalid}
                    inputMode="decimal"
                    placeholder="0,00 €"
                    value={customText}
                    onChange={(e) => onCustomChange(e.target.value)}
                    className="h-8 w-24 font-mono"
                  />
                )}
              </div>
              <p className={cn("text-[11px] h-4", customInvalid ? "text-coral" : "text-ink-2")}>
                {customInvalid ? "Betrag 0–1000 €, max. 2 Nachkommastellen" : "zieht den Betrag vom VK ab"}
              </p>
            </fieldset>

            {(data || kombiData) && (
              <div className="ml-auto self-center text-xs text-ink-2 flex items-center gap-1.5">
                {(preview.isFetching || kombiQuery.isFetching) && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
                <span>
                  {data ? folderTitle ?? "Kombi" : "Kombi"} · {data?.rows.length ?? 0} Artikel{kombiRequest && ` · ${kombiData?.rows.length ?? 0} Kombis`}
                </span>
              </div>
            )}
          </div>

          <KombiBuilder kombis={kombis} onChange={values => {
            setKombis(values);
            setKombiTiers(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => values.some(k => k.key === key))));
          }} />
          {kombiRequest && kombiQuery.error && <div role="alert" className="rounded-md border border-coral/40 bg-coral-soft px-4 py-2 text-sm text-coral">
            {kombiData ? "Kombi-Aktualisierung fehlgeschlagen — die angezeigten Werte sind veraltet" : "Kombi-Berechnung fehlgeschlagen"}: {errorText(kombiQuery.error)}
          </div>}
          {kombiRequest && kombiQuery.isLoading && <Notice>Berechne Kombi-Verkaufspreise…</Notice>}
          {kombiData?.missing.map(missing => <div key={missing.key} role="alert" className="rounded-md border border-warn/40 bg-warn-soft px-4 py-2 text-sm text-warn">
            {kombiLabel(kombis.find(k => k.key === missing.key)?.components ?? []) || missing.key}: unbekannte SKU {missing.skus.join(", ")} — Kombi nicht berechnet — zum Exportieren Kombi entfernen.
          </div>)}
          <Result
            hasFolder={node !== null}
            hasKombis={kombis.length > 0}
            tooBig={tooBig}
            folderCount={folderIds.length}
            isLoading={preview.isLoading}
            error={preview.error}
            stale={stale}
            data={data}
            kombiRows={kombiData?.rows ?? []}
            kombiNames={Object.fromEntries(kombis.map(k => [k.key, Object.fromEntries(k.components.map(c => [c.sku, c.name]))]))}
            effectiveKey={effectiveKey}
            optionen={tableOptionen}
            tiers={{ ...tiers, ...Object.fromEntries(Object.entries(kombiTiers).map(([key, tier]) => [`kombi:${key}`, tier])) }}
            onTierChange={(id, tier) => {
              const isKombi = id.startsWith("kombi:");
              const key = isKombi ? id.slice("kombi:".length) : id;
              (isKombi ? setKombiTiers : setTiers)((prev) => {
                const next = { ...prev };
                if (tier === "auto") delete next[key];
                else next[key] = tier;
                return next;
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}

function errorText(error: Error): string {
  return error instanceof ApiError ? error.message : "unbekannter Fehler";
}

function Notice({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "warn" | "bad" }) {
  return (
    <div
      className={cn(
        "rounded-md border px-4 py-6 text-sm text-center",
        tone === "neutral" && "border-line bg-panel text-ink-2",
        tone === "warn" && "border-warn/40 bg-warn-soft text-warn",
        tone === "bad" && "border-coral/40 bg-coral-soft text-coral",
      )}
    >
      {children}
    </div>
  );
}

function Result({
  hasFolder,
  hasKombis,
  tooBig,
  folderCount,
  isLoading,
  error,
  stale,
  data,
  kombiRows,
  kombiNames,
  effectiveKey,
  optionen,
  tiers,
  onTierChange,
}: {
  hasFolder: boolean;
  hasKombis: boolean;
  tooBig: boolean;
  folderCount: number;
  isLoading: boolean;
  error: Error | null;
  stale: boolean;
  data: SetPreviewResult | undefined;
  kombiRows: KombiPreviewRow[];
  kombiNames: Record<string, Record<string, string>>;
  effectiveKey: string;
  optionen: KundenversandOption[];
  tiers: Record<string, CarrierTier>;
  onTierChange: (sku: string, tier: CarrierTier) => void;
}) {
  if (!hasFolder && !hasKombis) {
    return (
      <Notice>
        <FolderSearch className="size-5 mx-auto mb-2 text-ink-2" aria-hidden="true" />
        Ordner wählen — die Preise gelten für alle Artikel des Ordners und seiner Unterordner.
      </Notice>
    );
  }
  // Fehlerhinweis vor dem Leerzustand: auch eine leere Vorantwort darf einen gescheiterten Neuabruf nicht verdecken.
  const staleAlert = stale && error && (
    <div role="alert" className="rounded-md border border-coral/40 bg-coral-soft px-4 py-2 text-sm text-coral">
      Aktualisierung fehlgeschlagen — die angezeigten Werte sind veraltet: {errorText(error)}
    </div>
  );
  return (
    <div className="space-y-3">
      {staleAlert}
      {tooBig && <Notice tone="warn">Unterbaum zu groß ({folderCount} Ordner) — bitte Unterordner wählen</Notice>}
      {hasFolder && !tooBig && error && !data && <Notice tone="bad">Berechnung fehlgeschlagen: {errorText(error)}</Notice>}
      {hasFolder && !tooBig && !error && (isLoading || !data) && <Notice>Berechne Verkaufspreise…</Notice>}
      {data?.rows.length === 0 && <Notice>Keine Artikel in diesem Ordner</Notice>}
      {data?.truncated && (
        <div className="rounded-md border border-warn/40 bg-warn-soft px-4 py-2 text-sm text-warn">
          Gekürzt: nur die ersten 500 Artikel werden gezeigt. Für alle Artikel einen Unterordner wählen.
        </div>
      )}
      {((data?.rows.length ?? 0) + kombiRows.length) > 0 && <SetPreviewTable
        rows={data?.rows ?? []}
        kombiRows={kombiRows}
        kombiNames={kombiNames}
        optionen={optionen}
        kundenversandKey={effectiveKey}
        tiers={tiers}
        onTierChange={onTierChange}
      />}
    </div>
  );
}
